import { t } from '../i18n';
/** Предпросмотр стиля субтитров на статичном кадре 9:16 с фотографией «говорящей головы». */
import { useEffect, useLayoutEffect, useId, useMemo, useRef, useState, type CSSProperties, type ReactElement } from 'react';
import { type HookAccent, type HookDesign, type SubtitleBox, type SubtitleSize, SUBTITLE_FONT_INFO, SUBTITLE_PRESET_FONT, SUBTITLE_TYPE_FRAME } from '@reels/shared/constants';
import type { SubtitlePreset } from '@reels/shared';
import { SUBTITLE_SIZE_FACTORS, SUBTITLE_SIZE_SCALE, captionAccentIndex, accentStart, accentEnd, subtitleGroups, subtitleWordCue, subtitleLayout, DYNAMIC_SUBTITLE_OPTIONS, capitalizeFirst, SUBTITLE_TRACK, hookLayout, subtitlePlacement, type SubtitlePosition } from '@reels/shared/typography';
import { useBandDrag } from '../lib/useBandDrag';
import { svgColorMatrix, type ColorFilter } from '@reels/shared/color';
import { groupIntoPhrases } from '@reels/shared/timeline';
import previewPhoto from '../assets/preview.webp';
import { HookOverlay } from './HookOverlay';

export interface SubtitlePreviewProps {
  preset: SubtitlePreset | 'off';
  position?: SubtitlePosition;
  animated?: boolean;
  hookText?: string;
  hookEnabled?: boolean;
  hookStyle?: HookDesign;
  hookAccent?: HookAccent;
  /** Подложка под строками субтитров, как в рендере; она же включает плашку хука. */
  subtitleBox?: SubtitleBox;
  /** Размер субтитров из настроек; хук не меняется. */
  subtitleSize?: SubtitleSize;
  sample?: string;
  /** Семейство шрифта субтитров; у хука отдельная плотная гарнитура. */
  fontFamily?: string;
  /** Цвет подсветки текущего слова, '#RRGGBB'. */
  highlight?: string;
  colorFilter?: ColorFilter;
  colorIntensity?: number;
  /** Если задан, субтитры в превью можно перетащить по вертикали. */
  onPositionChange?: (position: number) => void;
  /** Ручное положение хука (при ручном положении субтитров). */
  hookPosition?: number | null;
  /** Если задан, хук в превью можно перетащить по вертикали. */
  onHookPositionChange?: (position: number) => void;
}


export function SubtitlePreview({
  preset,
  position = 'bottom',
  animated = true,
  hookText,
  hookEnabled = false,
  hookStyle = 'card',
  hookAccent = 'normal',
  subtitleBox = 'none',
  subtitleSize = 'medium',
  sample = t('editor.preview.sample'),
  fontFamily = SUBTITLE_FONT_INFO[SUBTITLE_PRESET_FONT[preset === 'off' ? 'editorial' : preset]].family,
  highlight,
  colorFilter = 'off',
  colorIntensity = 70,
  onPositionChange,
  hookPosition = null,
  onHookPositionChange,
}: SubtitlePreviewProps): ReactElement {
  const filterId = `${useId().replace(/[^a-zA-Z0-9_-]/g, '')}-${colorFilter}-${colorIntensity}`;
  const [originalFor, setOriginalFor] = useState<string | null>(null);
  const original = originalFor === filterId;
  if (originalFor !== null && !original) setOriginalFor(null);
  const sampleWords = useMemo(() => sample.trim().split(/\s+/).filter(Boolean).map((text, index) => ({
    text, start: index * 0.42, end: (index + 1) * 0.42, tlStart: 0.3 + index * 0.42, tlEnd: 0.3 + (index + 1) * 0.42,
  })), [sample]);
  const pages = useMemo(() => groupIntoPhrases(sampleWords, DYNAMIC_SUBTITLE_OPTIONS), [sampleWords]);
  const [elapsed, setElapsed] = useState(0);
  const displayTime = animated ? elapsed : Infinity;
  const page = Number.isFinite(displayTime)
    ? pages.find((p, index) => displayTime < (pages[index + 1]?.start ?? Infinity))
    : pages.find(p => captionAccentIndex(p.words) >= 0) ?? pages[0];
  const words = page?.words ?? [];
  const layout = subtitleLayout(position);
  useEffect(() => {
    if (!animated) return;
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let timer: ReturnType<typeof setInterval> | undefined;
    const restart = () => {
      clearInterval(timer);
      setElapsed(motion.matches ? Infinity : 0);
      if (motion.matches || preset === 'off') return;
      const start = performance.now();
      const duration = (sampleWords.at(-1)?.tlEnd ?? 0) + 1.2;
      timer = setInterval(() => setElapsed(((performance.now() - start) / 1000) % duration), 33);
    };
    restart();
    motion.addEventListener('change', restart);
    return () => { clearInterval(timer); motion.removeEventListener('change', restart); };
  }, [sampleWords, preset, animated]);
  const accent = captionAccentIndex(words);
  const hotIndex = Number.isFinite(displayTime)
    ? words.findIndex((word, index) => displayTime >= word.tlStart && displayTime < (words[index + 1]?.tlStart ?? Infinity))
    : Math.max(0, accent);
  const firstAccent = accentStart(words, accent);
  const lastAccent = accentEnd(words, accent);
  const accentRef = useRef<HTMLSpanElement>(null);
  const captionRef = useRef<HTMLDivElement>(null);
  const [accentSize, setAccentSize] = useState(24);
  const accentText = words.slice(firstAccent, lastAccent + 1).map(w => w.text).join(' ').toLocaleUpperCase();
  // Кегли и интервалы шрифта из макета, как в рендере; размеры — до SUBTITLE_SIZE_SCALE, в долях ширины кадра.
  const profile = Object.values(SUBTITLE_FONT_INFO).find(f => f.family === fontFamily)?.type;
  const designWidth = (px: number) => px / SUBTITLE_TYPE_FRAME / SUBTITLE_SIZE_SCALE;
  const simpleProfiled = !!profile && preset !== 'editorial' && preset !== 'card' && preset !== 'off';
  const contextStyle = (hasAccent: boolean): CSSProperties | undefined => profile ? {
    fontSize: `${designWidth(hasAccent ? profile.context : profile.plain) * 100}cqw`,
    letterSpacing: `${hasAccent ? profile.contextTracking : profile.plainTracking}em`,
  } : undefined;
  const renderWords = (part: typeof words, uppercase = false) => subtitleGroups(part).map((group, groupIndex) => (
    <span key={group[0]!.tlStart}>
      {groupIndex > 0 ? ' ' : null}
      <span className="subtitle-word-group">{group.map((word, index) => {
        const wordIndex = words.indexOf(word);
        const hot = (preset === 'karaoke' || preset === 'bold') && wordIndex === hotIndex;
        const cased = uppercase || preset === 'bold' ? word.text.toLocaleUpperCase() : word.text;
        // «Минимал» начинает фразу с заглавной, Clean — строчными (CSS), как в рендере.
        const text = preset === 'minimal' && wordIndex === 0 ? capitalizeFirst(cased) : cased;
        const cue = subtitleWordCue({ ...word, text });
        return <span key={word.tlStart} className={hot ? 'typed-word hot' : 'typed-word'} style={{
          visibility: displayTime >= cue.at ? 'visible' : 'hidden',
          color: hot ? highlight ?? '#ffe500' : undefined,
        }}>
          {index > 0 ? ' ' : null}
          {cue.text}
        </span>;
      })}</span>
    </span>
  ));
  useEffect(() => {
    const element = accentRef.current;
    const container = element?.parentElement;
    if (!element || !container) return;
    let active = true;
    const measure = () => {
      if (!active) return;
      const ctx = document.createElement('canvas').getContext('2d');
      if (!ctx) return;
      const style = getComputedStyle(element);
      ctx.font = `${style.fontWeight} 100px ${style.fontFamily}`;
      const tracking = profile?.accentTracking ?? 0;
      const measured = ctx.measureText(accentText).width + tracking * 100 * Math.max(0, [...accentText].length - 1);
      const frame = container.closest('.frame')?.clientWidth ?? container.clientWidth;
      const cap = profile ? frame * designWidth(profile.accent) : container.clientWidth * 0.23;
      setAccentSize(Math.min(cap, container.clientWidth * 0.96 / Math.max(measured, 1) * 100));
    };
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    measure();
    void document.fonts.ready.then(measure);
    document.fonts.addEventListener('loadingdone', measure);
    return () => { active = false; observer.disconnect(); document.fonts.removeEventListener('loadingdone', measure); };
  }, [accentText, fontFamily, preset, profile]);

  // Fit the whole caption, including unbreakable word groups and card padding.
  // Hidden future words retain their size, so revealing them never moves the layout.
  useLayoutEffect(() => {
    const content = captionRef.current;
    const container = content?.parentElement;
    if (!content || !container) return;
    let active = true;
    const fit = () => {
      if (!active) return;
      content.style.transform = '';
      const style = getComputedStyle(container);
      const width = container.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      const height = container.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
      const box = content.getBoundingClientRect();
      const glyphs = [...content.querySelectorAll('.typed-word')].map(word => word.getBoundingClientRect());
      const left = Math.min(box.left, ...glyphs.map(glyph => glyph.left));
      const right = Math.max(box.right, ...glyphs.map(glyph => glyph.right));
      // Как в рендере: SUBTITLE_SIZE_SCALE с множителем размера — и для кегля, и для доли высоты
      // полосы; по ширине не шире самой полосы (рендер так же вписывает строки в безопасную зону).
      const share = SUBTITLE_SIZE_SCALE * SUBTITLE_SIZE_FACTORS[subtitleSize];
      const scale = Math.min(share * Math.min(1, height / Math.max(1, content.scrollHeight)), width / Math.max(1, right - left));
      // An overflowing inline word may start at the left edge instead of centering.
      // Recenter its entire painted extent after fitting, not just the parent box.
      const offset = ((box.left + box.right) / 2 - (left + right) / 2) * scale;
      content.style.transform = `translateX(${offset}px) scale(${scale})`;
    };
    const observer = new ResizeObserver(fit);
    observer.observe(container);
    observer.observe(content);
    fit();
    void document.fonts.ready.then(fit);
    document.fonts.addEventListener('loadingdone', fit);
    return () => { active = false; observer.disconnect(); document.fonts.removeEventListener('loadingdone', fit); };
  }, [page?.text, fontFamily, preset, position, accentSize, subtitleSize]);

  // Отступы плашки как в рендере: не меньше глубины хвостов шрифта, сверху ещё запас на буквы выше
  // заглавных («б» у Playfair). Плашка обрезана по верху заглавных и базовой линии (text-box в CSS).
  const boxPadding = (text: string): CSSProperties | undefined => {
    if (subtitleBox === 'none') return undefined;
    const ctx = document.createElement('canvas').getContext('2d');
    if (!ctx) return undefined;
    ctx.font = `800 100px "${fontFamily}"`;
    const cap = ctx.measureText('НЕТ').actualBoundingBoxAscent / 100;
    const ink = ctx.measureText(text).actualBoundingBoxAscent / 100;
    const descent = ctx.measureText('урдДЦЩф,;').actualBoundingBoxDescent / 100;
    const pad = Math.max(0.12, descent + 0.03);
    return { paddingTop: `${pad + Math.max(0, ink - cap)}em`, paddingBottom: `${pad}em` };
  };
  const lineText = (part: typeof words, uppercase = false) => {
    const text = part.map(w => w.text).join(' ');
    return uppercase || preset === 'bold' ? text.toLocaleUpperCase() : preset === 'clean' || preset === 'karaoke' ? text.toLocaleLowerCase() : text;
  };

  // Ручной режим: субтитры и хук перетаскивают в превью.
  const subtitleDrag = useBandDrag(subtitlePlacement(position), preset !== 'off' ? onPositionChange : undefined, t('editor.position.title'));
  const hookBand = hookLayout(position, preset !== 'off', hookPosition);
  const hookPlacement = (hookBand.top - SUBTITLE_TRACK.minTop) / (SUBTITLE_TRACK.maxTop - SUBTITLE_TRACK.minTop);
  const hookDrag = useBandDrag(hookPlacement, hookEnabled && hookText ? onHookPositionChange : undefined, t('editor.position.hook'));

  return (
    <div
      className="frame"
      aria-label={t('editor.preview.aria')}
      style={{ fontFamily: fontFamily ? `'${fontFamily}', sans-serif` : undefined,
        '--subtitle-center': `${layout.centerY * 100}%`,
        '--subtitle-band': `${layout.bandHeight * 100}%`,
        '--subtitle-bottom': `${layout.bottom * 100}%`,
        '--subtitle-left': `${layout.left * 100}%`,
        '--subtitle-right': `${(1 - layout.right) * 100}%`,
      } as CSSProperties}
    >
      <svg key={filterId} className="color-filter-defs" aria-hidden="true" width="0" height="0">
        <defs><filter id={filterId} colorInterpolationFilters="sRGB">
          <feColorMatrix type="matrix" values={svgColorMatrix(colorFilter, colorIntensity)} />
        </filter></defs>
      </svg>
      <img className="frame-photo" src={previewPhoto} alt=""
        style={colorFilter !== 'off' && !original ? { filter: `url(#${filterId})` } : undefined} />
      {colorFilter !== 'off' ? <button type="button" className="compare-original" aria-pressed={original}
        onClick={() => setOriginalFor(original ? null : filterId)}>{original ? t('editor.color.showFilter') : t('editor.color.compare')}</button> : null}
      {hookEnabled && hookText ? (
        <HookOverlay design={hookStyle} accentMode={hookAccent} fontFamily={fontFamily} text={hookText} position={position} hookPosition={hookPosition} box={subtitleBox === 'none' ? false : subtitleBox} subtitlesEnabled={preset !== 'off'} accent={highlight} />
      ) : null}
      {hookDrag.handle ? <div className={'hook-drag' + (hookDrag.dragging ? ' dragging' : '')} {...hookDrag.dragProps}
        style={{ top: `${hookBand.top * 100}%`, height: `${hookBand.bandHeight * 100}%`, left: `${hookBand.left * 100}%`, right: `${(1 - hookBand.right) * 100}%` }}>
        {hookDrag.handle}
      </div> : null}
      <div aria-label={page?.text ?? sample} {...subtitleDrag.dragProps}
        className={`sub ${preset === 'card' ? 'caption-card' : preset}${subtitleBox !== 'none' ? ` boxed box-${subtitleBox}` : ''}${simpleProfiled ? ' profiled' : ''}${subtitleDrag.handle ? ' draggable' : ''}${subtitleDrag.dragging ? ' dragging' : ''}`} style={simpleProfiled ? {
        // Простые стили со шрифтом из макета: кегль и интервал «обычной строки», как в рендере.
        fontSize: `${designWidth(profile!.plain) * 100}cqw`,
        letterSpacing: `${profile!.plainTracking + (preset === 'bold' ? -0.01 : 0)}em`,
      } : preset === 'minimal' || preset === 'card' ? {
        fontSize: `${7.778 * (Object.values(SUBTITLE_FONT_INFO).find(f => f.family === fontFamily)?.assScale ?? 1)}cqw`,
      } : undefined}>
        {subtitleDrag.handle}
        <div ref={captionRef} className="subtitle-content">
        {preset === 'editorial' && accent >= 0 ? (
          <>
            {firstAccent > 0 ? <span className="type-context" style={contextStyle(true)}><span className="type-line" style={boxPadding(lineText(words.slice(0, firstAccent)))}>{renderWords(words.slice(0, firstAccent))}</span></span> : null}
            <span ref={accentRef} className="type-accent" style={{ color: highlight, fontSize: accentSize,
              ...(profile ? { letterSpacing: `${profile.accentTracking}em` } : {}) }}><span className="type-line" style={boxPadding(lineText(words.slice(firstAccent, lastAccent + 1), true))}>{renderWords(words.slice(firstAccent, lastAccent + 1), true)}</span></span>
            {lastAccent < words.length - 1 ? <span className="type-context after" style={contextStyle(true)}><span className="type-line" style={boxPadding(lineText(words.slice(lastAccent + 1)))}>{renderWords(words.slice(lastAccent + 1))}</span></span> : null}
          </>
        ) : <span className={preset === 'editorial' ? 'type-context' : 'caption-text'} style={preset === 'editorial' ? contextStyle(false) : boxPadding(lineText(words))}>{preset === 'editorial' ? <span className="type-line" style={boxPadding(lineText(words))}>{renderWords(words)}</span> : renderWords(words)}</span>}
        </div>
      </div>
    </div>
  );
}
